import { Button, Card, Form, Input, Select, Space, message } from 'antd';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useEffect } from 'react';
import api from '@/services/api.service';
import EducationPageChrome from '@/pages/education/components/EducationPageChrome';

type ApiError = { response?: { data?: { message?: string } } };
type Application = { id: string; record_version: number };

function asList<T>(payload: unknown): T[] {
  if (Array.isArray(payload)) return payload as T[];
  return [];
}

const STATUS_OPTIONS = [
  { value: 'under_review', label: 'under_review' },
  { value: 'assessment_scheduled', label: 'assessment_scheduled' },
  { value: 'decision_pending', label: 'decision_pending' },
  { value: 'waitlisted', label: 'waitlisted' },
  { value: 'rejected', label: 'rejected' },
];

export default function AdmissionReviewPage() {
  const { id = '' } = useParams();
  const [params] = useSearchParams();
  const { t } = useTranslation('education');
  const navigate = useNavigate();
  const [moveForm] = Form.useForm();

  const application = useQuery({
    queryKey: ['education-admission-version', id],
    enabled: Boolean(id),
    queryFn: async () => {
      const rows = asList<Application>((await api.get('/education/admissions')).data);
      return rows.find((row) => row.id === id) || null;
    },
  });

  useEffect(() => {
    const version = application.data?.record_version;
    if (version != null) moveForm.setFieldValue('record_version', version);
  }, [application.data?.record_version, moveForm]);

  const move = useMutation({
    mutationFn: (values: { status: string; record_version: number }) =>
      api.post(`/education/admissions/${id}/transition`, {
        ...values,
        record_version: values.record_version ?? application.data?.record_version,
      }),
    onSuccess: () => message.success(t('saved')),
    onError: (error: ApiError) => message.error(error.response?.data?.message || t('unavailable', { status: '' })),
  });

  const assessment = useMutation({
    mutationFn: (values: { scheduled_at?: string; result_status?: string }) =>
      api.post(`/education/admissions/${id}/assessments`, values),
    onSuccess: () => message.success(t('saved')),
    onError: (error: ApiError) => message.error(error.response?.data?.message || t('unavailable', { status: '' })),
  });

  return (
    <EducationPageChrome
      title={t('review')}
      description={t('admissionsIntro')}
      extra={
        <Button onClick={() => navigate(`/education/admissions/${id}?${params.toString()}`)}>
          {t('backToAdmissions')}
        </Button>
      }
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Card size="small" title={t('status')} loading={application.isLoading}>
          <Form form={moveForm} layout="vertical" onFinish={(values) => move.mutate(values)} style={{ maxWidth: 480 }}>
            <Form.Item name="status" label={t('status')} rules={[{ required: true }]}>
              <Select options={STATUS_OPTIONS} showSearch optionFilterProp="label" />
            </Form.Item>
            <Form.Item name="record_version" hidden><Input /></Form.Item>
            <Button htmlType="submit" loading={move.isPending}>{t('save')}</Button>
          </Form>
        </Card>
        <Card size="small" title={t('review')}>
          <Form layout="vertical" onFinish={(values) => assessment.mutate(values)} style={{ maxWidth: 480 }}>
            <Form.Item name="scheduled_at" label={t('startDate')}><Input placeholder="YYYY-MM-DD" /></Form.Item>
            <Form.Item name="result_status" label={t('status')}>
              <Select
                allowClear
                options={[
                  { value: 'pass', label: 'pass' },
                  { value: 'fail', label: 'fail' },
                  { value: 'pending', label: 'pending' },
                ]}
              />
            </Form.Item>
            <Button htmlType="submit" loading={assessment.isPending}>{t('review')}</Button>
          </Form>
        </Card>
      </Space>
    </EducationPageChrome>
  );
}
